import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, LineChart, Line } from 'recharts';
import { Calendar, Clock, TrendingUp, AlertCircle, ArrowRight } from 'lucide-react';
import { BathroomVisit } from '../BathroomSafety';

// Mock data generation for bathroom usage patterns
const generateMockUsagePatterns = (): BathroomVisit[] => {
  const visits: BathroomVisit[] = [];
  const now = new Date();
  
  // Generate data for the past 30 days with specific patterns
  for (let i = 0; i < 30; i++) {
    const day = new Date();
    day.setDate(now.getDate() - i);
    
    // Weekday pattern (more morning/evening visits)
    if (day.getDay() !== 0 && day.getDay() !== 6) {
      // Morning routine (6-8AM)
      const morningHour = 6 + Math.floor(Math.random() * 2);
      const morningMinute = Math.floor(Math.random() * 60);
      
      const morningVisit = new Date(day);
      morningVisit.setHours(morningHour, morningMinute);
      
      visits.push({
        id: `pattern-${i}-morning`,
        timestamp: morningVisit,
        duration: (5 + Math.floor(Math.random() * 5)) * 60, // 5-10 minutes
        movementPattern: Math.random() < 0.1 ? 'unstable' : 'normal',
        pressureDistribution: 'balanced',
        waterDetected: Math.random() < 0.1,
        exitStatus: 'normal',
        isNighttime: false
      });
      
      // Evening routine (8-10PM)
      const eveningHour = 20 + Math.floor(Math.random() * 2);
      const eveningMinute = Math.floor(Math.random() * 60);
      
      const eveningVisit = new Date(day);
      eveningVisit.setHours(eveningHour, eveningMinute);
      
      visits.push({
        id: `pattern-${i}-evening`,
        timestamp: eveningVisit,
        duration: (5 + Math.floor(Math.random() * 5)) * 60, // 5-10 minutes
        movementPattern: Math.random() < 0.15 ? 'unstable' : 'normal',
        pressureDistribution: 'balanced',
        waterDetected: Math.random() < 0.1,
        exitStatus: 'normal',
        isNighttime: true
      });
    } 
    // Weekend pattern (more scattered throughout day)
    else {
      const visitsPerDay = 3 + Math.floor(Math.random() * 3);
      for (let j = 0; j < visitsPerDay; j++) {
        const hour = 8 + Math.floor(Math.random() * 14); // 8AM - 10PM
        const minute = Math.floor(Math.random() * 60);
        
        const timestamp = new Date(day);
        timestamp.setHours(hour, minute);
        
        visits.push({
          id: `pattern-${i}-${j}`,
          timestamp,
          duration: (4 + Math.floor(Math.random() * 6)) * 60, // 4-10 minutes
          movementPattern: Math.random() < 0.1 ? 'unstable' : 'normal',
          pressureDistribution: 'balanced',
          waterDetected: Math.random() < 0.1,
          exitStatus: 'normal',
          isNighttime: hour >= 20
        });
      }
    }
    
    // Add some mid-day visits (with higher probability on weekends)
    const addMiddayVisit = Math.random() < (day.getDay() === 0 || day.getDay() === 6 ? 0.8 : 0.4);
    
    if (addMiddayVisit) {
      const middayHour = 12 + Math.floor(Math.random() * 5); // 12-5PM
      const middayMinute = Math.floor(Math.random() * 60);
      
      const middayVisit = new Date(day);
      middayVisit.setHours(middayHour, middayMinute);
      
      visits.push({
        id: `pattern-${i}-midday`,
        timestamp: middayVisit,
        duration: (3 + Math.floor(Math.random() * 5)) * 60, // 3-8 minutes
        movementPattern: 'normal',
        pressureDistribution: 'balanced',
        waterDetected: Math.random() < 0.1,
        exitStatus: 'normal',
        isNighttime: false
      });
    }
    
    // Add occasional nighttime visits (higher probability for older people)
    const addNighttimeVisit = Math.random() < 0.35; // 35% chance
    
    if (addNighttimeVisit) {
      const nightHour = Math.floor(Math.random() * 5); // 12-5AM
      const nightMinute = Math.floor(Math.random() * 60);
      
      const nightVisit = new Date(day);
      nightVisit.setHours(nightHour, nightMinute);
      
      visits.push({
        id: `pattern-${i}-night`,
        timestamp: nightVisit,
        duration: (2 + Math.floor(Math.random() * 3)) * 60, // 2-5 minutes
        movementPattern: Math.random() < 0.25 ? 'unstable' : 'normal',
        pressureDistribution: 'balanced',
        waterDetected: Math.random() < 0.05,
        exitStatus: 'normal',
        isNighttime: true
      });
    }
  }
  
  // Sort by timestamp
  return visits.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
};

const mockUsagePatterns = generateMockUsagePatterns();

// Prepare data for weekly pattern view
const prepareWeekdayPatterns = (visits: BathroomVisit[]) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayData = days.map(day => ({
    day,
    morning: 0, // 5AM-12PM
    afternoon: 0, // 12PM-5PM
    evening: 0, // 5PM-10PM
    night: 0 // 10PM-5AM
  }));
  
  visits.forEach(visit => {
    const dayIndex = visit.timestamp.getDay();
    const hour = visit.timestamp.getHours();
    
    if (hour >= 5 && hour < 12) {
      dayData[dayIndex].morning++;
    } else if (hour >= 12 && hour < 17) {
      dayData[dayIndex].afternoon++;
    } else if (hour >= 17 && hour < 22) {
      dayData[dayIndex].evening++;
    } else {
      dayData[dayIndex].night++;
    }
  });
  
  return dayData;
};

// Prepare data for hourly distribution
const prepareHourlyDistribution = (visits: BathroomVisit[]) => {
  const hourData = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    count: 0,
    formattedHour: `${i === 0 ? 12 : i > 12 ? i - 12 : i}${i < 12 ? 'AM' : 'PM'}`
  }));
  
  visits.forEach(visit => {
    const hour = visit.timestamp.getHours();
    hourData[hour].count++;
  });
  
  return hourData;
};

// Prepare data for duration averages
const prepareDurationByTimeOfDay = (visits: BathroomVisit[]) => {
  // Group into four segments
  const segments = [
    { name: 'Morning (5AM-12PM)', durations: [] as number[] },
    { name: 'Afternoon (12PM-5PM)', durations: [] as number[] },
    { name: 'Evening (5PM-10PM)', durations: [] as number[] },
    { name: 'Night (10PM-5AM)', durations: [] as number[] }
  ];
  
  visits.forEach(visit => {
    const hour = visit.timestamp.getHours();
    
    if (hour >= 5 && hour < 12) {
      segments[0].durations.push(visit.duration);
    } else if (hour >= 12 && hour < 17) {
      segments[1].durations.push(visit.duration);
    } else if (hour >= 17 && hour < 22) {
      segments[2].durations.push(visit.duration);
    } else {
      segments[3].durations.push(visit.duration);
    }
  });
  
  // Calculate averages
  return segments.map(segment => ({
    name: segment.name,
    averageDuration: segment.durations.length > 0 
      ? Math.round(segment.durations.reduce((a, b) => a + b, 0) / segment.durations.length / 60 * 10) / 10 // in minutes, rounded to 1 decimal
      : 0,
    visits: segment.durations.length
  }));
};

// Detect anomalies in patterns
const detectAnomalies = (visits: BathroomVisit[]) => {
  const anomalies = [];
  
  // Early morning frequency anomaly
  const earlyMorningVisits = visits.filter(v => {
    const hour = v.timestamp.getHours();
    return hour >= 0 && hour < 5;
  }).length;
  
  const totalDays = new Set(visits.map(v => v.timestamp.toDateString())).size;
  const earlyMorningAvg = earlyMorningVisits / totalDays;
  
  if (earlyMorningAvg > 0.4) { // More than 0.4 early morning visits per night on average
    anomalies.push({
      type: 'frequency',
      description: 'Higher than normal nighttime bathroom visits',
      impact: 'May indicate urinary issues or medication side effects',
      severity: earlyMorningAvg > 0.7 ? 'high' : 'medium'
    });
  }
  
  // Duration anomaly
  const allDurations = visits.map(v => v.duration);
  const avgDuration = allDurations.reduce((a, b) => a + b, 0) / allDurations.length;
  const longVisits = visits.filter(v => v.duration > avgDuration * 1.5).length;
  const longVisitPercentage = (longVisits / visits.length) * 100;
  
  if (longVisitPercentage > 15) { // More than 15% of visits are significantly longer than average
    anomalies.push({
      type: 'duration',
      description: 'Unusually long bathroom visits detected',
      impact: 'May indicate difficulty with mobility or digestive issues',
      severity: longVisitPercentage > 25 ? 'high' : 'medium'
    });
  }
  
  // Timing consistency anomaly
  const morningVisitHours = visits
    .filter(v => v.timestamp.getHours() >= 5 && v.timestamp.getHours() < 10)
    .map(v => v.timestamp.getHours() + (v.timestamp.getMinutes() / 60));
  
  if (morningVisitHours.length >= 10) { // Need enough data points
    // Calculate standard deviation of morning visit times
    const avgMorningHour = morningVisitHours.reduce((a, b) => a + b, 0) / morningVisitHours.length;
    const variance = morningVisitHours.reduce((a, b) => a + Math.pow(b - avgMorningHour, 2), 0) / morningVisitHours.length;
    const stdDev = Math.sqrt(variance);
    
    if (stdDev > 1.5) { // High variation in morning routine timing
      anomalies.push({
        type: 'consistency',
        description: 'Inconsistent morning bathroom routine',
        impact: 'May indicate disrupted sleep or daily routine changes',
        severity: 'medium'
      });
    }
  }
  
  return anomalies;
};

const BathroomUsagePatterns: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('month');
  
  // Filter visits based on selected time range
  const filteredVisits = mockUsagePatterns.filter(visit => {
    const now = new Date();
    const timeLimit = new Date();
    
    if (timeRange === 'week') {
      timeLimit.setDate(now.getDate() - 7);
    } else if (timeRange === 'month') {
      timeLimit.setDate(now.getDate() - 30);
    } else {
      timeLimit.setDate(now.getDate() - 90);
    }
    
    return visit.timestamp >= timeLimit;
  });
  
  const weekdayPatterns = prepareWeekdayPatterns(filteredVisits);
  const hourlyDistribution = prepareHourlyDistribution(filteredVisits);
  const durationByTimeOfDay = prepareDurationByTimeOfDay(filteredVisits);
  const anomalies = detectAnomalies(filteredVisits);
  
  // Calculate average daily visits
  const uniqueDays = new Set(filteredVisits.map(v => v.timestamp.toDateString())).size;
  const avgDailyVisits = filteredVisits.length / uniqueDays;
  
  // Calculate most active day
  const dayVisitCounts = weekdayPatterns.map(d => ({
    day: d.day,
    total: d.morning + d.afternoon + d.evening + d.night
  }));
  const mostActiveDay = dayVisitCounts.sort((a, b) => b.total - a.total)[0];
  
  // Calculate most common time
  const mostCommonHour = hourlyDistribution.sort((a, b) => b.count - a.count)[0];
  
  return (
    <div className="p-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-2">
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'week' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('week')}
          >
            Past Week
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'month' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('month')}
          >
            Past Month
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'quarter' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('quarter')}
          >
            Past 3 Months
          </button>
        </div>
        
        {anomalies.length > 0 && (
          <div className="px-4 py-2 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            <span>{anomalies.length} pattern {anomalies.length === 1 ? 'anomaly' : 'anomalies'} detected</span>
          </div>
        )}
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-purple-900">Avg. Daily Visits</h3>
              <p className="text-3xl font-bold text-purple-700">{avgDailyVisits.toFixed(1)}</p>
            </div>
            <Calendar className="text-purple-500" />
          </div>
          <p className="mt-2 text-sm text-purple-600">
            {avgDailyVisits > 6 ? 'Above average frequency' : 'Within normal range'}
          </p>
        </div>
        
        <div className="bg-fuchsia-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-fuchsia-900">Most Active Day</h3>
              <p className="text-3xl font-bold text-fuchsia-700">{mostActiveDay?.day}</p>
            </div>
            <Calendar className="text-fuchsia-500" />
          </div>
          <p className="mt-2 text-sm text-fuchsia-600">
            {mostActiveDay?.total} visits on average
          </p>
        </div>
        
        <div className="bg-indigo-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-indigo-900">Peak Usage Time</h3>
              <p className="text-3xl font-bold text-indigo-700">{mostCommonHour?.formattedHour}</p>
            </div>
            <Clock className="text-indigo-500" />
          </div>
          <p className="mt-2 text-sm text-indigo-600">
            {mostCommonHour?.count} visits during this hour
          </p>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">Weekly Patterns</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={weekdayPatterns} barSize={20}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="morning" name="Morning (5AM-12PM)" stackId="a" fill="#c084fc" />
              <Bar dataKey="afternoon" name="Afternoon (12PM-5PM)" stackId="a" fill="#8b5cf6" />
              <Bar dataKey="evening" name="Evening (5PM-10PM)" stackId="a" fill="#6366f1" />
              <Bar dataKey="night" name="Night (10PM-5AM)" stackId="a" fill="#4f46e5" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">Hourly Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={hourlyDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="formattedHour" 
                ticks={[
                  '12AM', '3AM', '6AM', '9AM', '12PM', '3PM', '6PM', '9PM'
                ]}
              />
              <YAxis />
              <Tooltip />
              <Area 
                type="monotone" 
                dataKey="count" 
                name="Visits" 
                stroke="#8b5cf6" 
                fill="#c084fc" 
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Duration Analysis */}
      <div className="bg-white rounded-lg p-4 shadow-sm mb-6">
        <h3 className="text-lg font-medium mb-4">Visit Duration by Time of Day</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={durationByTimeOfDay}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis yAxisId="left" orientation="left" stroke="#8b5cf6" />
            <YAxis yAxisId="right" orientation="right" stroke="#6366f1" />
            <Tooltip />
            <Legend />
            <Bar 
              yAxisId="left" 
              dataKey="averageDuration" 
              name="Avg. Duration (minutes)" 
              fill="#8b5cf6"
            />
            <Line 
              yAxisId="right" 
              type="monotone" 
              dataKey="visits" 
              name="Number of Visits" 
              stroke="#6366f1" 
              strokeWidth={2}
            />
          </BarChart>
        </ResponsiveContainer>
        <p className="mt-3 text-sm text-gray-600">
          {durationByTimeOfDay.sort((a, b) => b.averageDuration - a.averageDuration)[0]?.name} has the longest average visit duration
        </p>
      </div>
      
      {/* Anomalies and Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <AlertCircle className="mr-2 text-yellow-500" />
            Pattern Anomalies
          </h3>
          
          {anomalies.length > 0 ? (
            <div className="space-y-4">
              {anomalies.map((anomaly, index) => (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg ${
                    anomaly.severity === 'high' ? 'bg-red-50' : 'bg-yellow-50'
                  }`}
                >
                  <div className="flex justify-between">
                    <h4 className={`font-medium ${
                      anomaly.severity === 'high' ? 'text-red-900' : 'text-yellow-900'
                    }`}>
                      {anomaly.description}
                    </h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      anomaly.severity === 'high' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {anomaly.severity}
                    </span>
                  </div>
                  <p className={`mt-1 text-sm ${
                    anomaly.severity === 'high' ? 'text-red-700' : 'text-yellow-700'
                  }`}>
                    {anomaly.impact}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-green-800">No significant pattern anomalies detected in the selected time period.</p>
            </div>
          )}
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">Insights & Recommendations</h3>
          <div className="space-y-4">
            <div className="flex">
              <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-purple-100 text-purple-600">
                <TrendingUp size={18} />
              </div>
              <div className="ml-4">
                <h4 className="text-sm font-medium text-gray-900">Pattern Analysis</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Your bathroom usage shows a consistent pattern, primarily concentrated in the morning and evening hours.
                </p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                <Clock size={18} />
              </div>
              <div className="ml-4">
                <h4 className="text-sm font-medium text-gray-900">Regular Timing</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Most bathroom visits occur between 7-8AM and 9-10PM, aligning with typical morning and evening routines.
                </p>
              </div>
            </div>
            
            {avgDailyVisits > 6 && (
              <div className="flex">
                <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <AlertCircle size={18} />
                </div>
                <div className="ml-4">
                  <h4 className="text-sm font-medium text-gray-900">Frequency Consideration</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Your daily bathroom visit frequency is slightly above average. Consider discussing with your healthcare provider.
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex">
              <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <ArrowRight size={18} />
              </div>
              <div className="ml-4">
                <h4 className="text-sm font-medium text-gray-900">Next Steps</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Continue monitoring for significant changes in patterns, especially early morning or frequent nighttime visits.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BathroomUsagePatterns; 